import React, { useState, useEffect, useRef } from 'react';
import { Mail, RefreshCw, LogOut, CheckCircle2, AlertCircle } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { sendEmailVerification } from 'firebase/auth';
import { functions } from '../config/firebase';
import logoHeaderWhite from '../assets/logo-header-white.png';

/**
 * AdminEmailVerificationScreen
 * Schermata di blocco per gli amministratori che non hanno ancora confermato
 * il proprio indirizzo email dopo la registrazione con email e password.
 * 
 * - Invia l'email con template personalizzato via Brevo
 * - Fallback automatico su sendEmailVerification di Firebase
 * - Polling ogni 3 secondi con user.reload(): non appena l'utente clicca il link
 *   nella mail, la schermata si sblocca automaticamente senza bisogno di refresh
 * - Cooldown di 60s sul pulsante di reinvio
 * - Opzione di disconnessione (onLogout) per accedere con un altro account
 */
export default function AdminEmailVerificationScreen({ user, onLogout }) {
  const [resendCooldown, setResendCooldown] = useState(45);
  const [attempts, setAttempts] = useState(0);
  const [sending, setSending] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [checking, setChecking] = useState(false);
  const cooldownTimerRef = useRef(null);
  const pollTimerRef = useRef(null);

  const sendVerificationFnRef = useRef(httpsCallable(functions, 'sendCustomerVerificationEmail'));

  const startCooldown = (seconds = 60) => {
    setResendCooldown(seconds);
    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    cooldownTimerRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(cooldownTimerRef.current);
          cooldownTimerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendEmail = async () => {
    if (!user || sending || resendCooldown > 0) return;
    setSending(true);
    setStatusMsg('');
    setErrorMsg('');
    const nextAttempts = attempts + 1;
    setAttempts(nextAttempts);

    try {
      const continueUrl = 'https://noleggioincloud.app/admin';
      const res = await sendVerificationFnRef.current({ continueUrl });
      if (res?.data?.sent) {
        setStatusMsg('Email inviata con successo! Controlla la posta in arrivo o la cartella Spam.');
      } else {
        // Fallback Firebase nativo
        await sendEmailVerification(user, { url: 'https://noleggioincloud.app/admin', handleCodeInApp: false });
        setStatusMsg('Email inviata! Controlla sia la posta in arrivo che la cartella Spam/Posta indesiderata.');
      }
      startCooldown(60);
    } catch (err) {
      console.warn('Errore Cloud Function verifica:', err);
      const errCode = err?.code || '';
      const errMsg = err?.message || '';
      const isTooMany = errCode.includes('resource-exhausted') || 
                        errMsg.includes('TOO_MANY_ATTEMPTS_TRY_LATER') || 
                        errCode.includes('too-many-requests');

      if (isTooMany) {
        startCooldown(60);
        if (nextAttempts >= 3) {
          setErrorMsg('Troppi tentativi recenti per questo indirizzo. Attendi 5-10 minuti prima di richiedere un nuovo invio.');
        } else {
          setStatusMsg("L'email di verifica è già stata inviata di recente. Attendi 1 o 2 minuti e controlla anche la cartella Spam.");
        }
        return;
      }

      try {
        await sendEmailVerification(user, { url: 'https://noleggioincloud.app/admin', handleCodeInApp: false });
        setStatusMsg('Email inviata! Controlla la cartella Spam o Posta indesiderata.');
        startCooldown(60);
      } catch (fallbackErr) {
        console.error('Errore fallback invio verifica:', fallbackErr);
        const code = fallbackErr?.code || '';
        const isFallbackTooMany = code.includes('too-many-requests') || (fallbackErr?.message || '').includes('TOO_MANY_ATTEMPTS_TRY_LATER');
        
        startCooldown(60);
        if (isFallbackTooMany) {
          if (nextAttempts >= 3) {
            setErrorMsg('Troppi tentativi recenti. Attendi 5-10 minuti prima di richiedere un nuovo invio.');
          } else {
            setStatusMsg("L'email di verifica è già stata inviata di recente. Attendi 1 o 2 minuti e controlla anche la cartella Spam.");
          }
        } else {
          setErrorMsg("Non è stato possibile inviare l'email. Riprova tra qualche istante.");
        }
      }
    } finally {
      setSending(false);
    }
  };

  // Al montaggio, l'email è già stata inviata all'atto della registrazione.
  // Avviamo un cooldown iniziale di 45s senza forzare una seconda chiamata istantanea che causerebbe il blocco Firebase.
  useEffect(() => {
    startCooldown(45);
    return () => {
      if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    };
  }, []);

  // Polling automatico: ogni 3 secondi controlla se l'utente ha confermato l'email
  useEffect(() => {
    if (!user) return;
    pollTimerRef.current = setInterval(async () => {
      try {
        await user.reload();
        if (user.emailVerified) {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
          window.location.reload();
        }
      } catch (_e) {
        // ignora errori temporanei di rete
      }
    }, 3000);

    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    };
  }, [user]);

  const handleManualCheck = async () => {
    if (!user || checking) return;
    setChecking(true);
    setErrorMsg('');
    setStatusMsg('');
    try {
      await user.reload();
      if (user.emailVerified) {
        window.location.reload();
      } else {
        setErrorMsg('L\'indirizzo email non risulta ancora confermato. Clicca sul link che hai ricevuto via email prima di continuare.');
      }
    } catch (_err) {
      setErrorMsg('Errore durante la verifica dello stato. Riprova tra poco.');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-4 sm:p-6">
      {/* Header Logo */}
      <div className="mb-8">
        <img src={logoHeaderWhite} alt="NoleggioinCloud" style={{ height: 36, width: 'auto' }} />
      </div>

      {/* Card principale */}
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-6 sm:p-8 text-center border border-slate-100">
        <div
          className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner"
          style={{ background: '#EEF2FF' }}
        >
          <Mail className="w-10 h-10 text-blue-600" />
        </div>

        <h1 className="text-2xl font-black text-slate-900 tracking-tight mb-3">
          Conferma il tuo indirizzo email
        </h1>

        <p className="text-slate-600 mb-3 text-sm sm:text-base leading-relaxed">
          Abbiamo inviato un link di verifica a:
          <br />
          <strong className="text-slate-900 font-bold text-base inline-block mt-1 bg-slate-100 px-3 py-1 rounded-lg">
            {user?.email || 'il tuo indirizzo'}
          </strong>
        </p>

        <p className="text-slate-500 text-xs sm:text-sm mb-6 leading-relaxed">
          Clicca sul link presente nell'email per confermare la tua identità e sbloccare l'accesso al pannello gestionale.
        </p>

        {/* Indicatore di attesa live */}
        <div className="flex items-center justify-center gap-2 p-3 rounded-2xl bg-blue-50 border border-blue-100 mb-6">
          <span className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin flex-shrink-0" />
          <p className="text-xs font-semibold text-blue-900">
            In attesa di conferma… la pagina si aggiornerà automaticamente dopo il click.
          </p>
        </div>

        {/* Messaggi di feedback */}
        {statusMsg && (
          <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs text-left mb-5">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-emerald-600" />
            <span>{statusMsg}</span>
          </div>
        )}

        {errorMsg && (
          <div className="flex items-center gap-2 p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs text-left mb-5">
            <AlertCircle className="w-4 h-4 flex-shrink-0 text-rose-600" />
            <span>{errorMsg}</span>
          </div>
        )}

        {/* Azioni */}
        <div className="space-y-3">
          <button
            type="button"
            onClick={handleManualCheck}
            disabled={checking}
            className="w-full py-3.5 rounded-2xl text-white text-sm font-bold transition shadow-sm hover:opacity-95 disabled:opacity-50"
            style={{ background: '#2563EB' }}
          >
            {checking ? 'Controllo in corso…' : 'Ho già confermato l\'email →'}
          </button>

          <button
            type="button"
            onClick={handleSendEmail}
            disabled={sending || resendCooldown > 0}
            className="w-full py-3 rounded-2xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`w-4 h-4 ${sending ? 'animate-spin' : ''}`} />
            {sending ? 'Invio in corso…' : resendCooldown > 0 ? `Rinvia tra ${resendCooldown}s` : 'Rinvia email di verifica'}
          </button>
        </div>

        {/* Consigli cartella spam */}
        <div className="mt-6 pt-5 border-t border-slate-100 text-left">
          <p className="text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wide">Non trovi l'email?</p>
          <ul className="text-xs text-slate-500 space-y-1.5 list-disc list-inside">
            <li>Controlla la cartella <strong>Spam o Posta indesiderata</strong> (cerca <code>noleggioincloud</code>)</li>
            <li>Attendi 1 o 2 minuti per la consegna del messaggio</li>
            <li>Assicurati che l'indirizzo indicato sopra sia corretto</li>
          </ul>
        </div>

        {/* Logout / cambio account */}
        {onLogout && (
          <div className="mt-6 pt-4 border-t border-slate-100 flex justify-center">
            <button
              type="button"
              onClick={onLogout}
              className="text-xs text-slate-400 hover:text-rose-600 font-semibold flex items-center gap-1.5 transition"
            >
              <LogOut className="w-3.5 h-3.5" />
              Esci o accedi con un altro account
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
